import "./site/error-beacon";
import { Splinter, SplinterResult, RUN_MS } from "./games/splinter/splinter";
import { recordRun, showXpToast } from "./site/profile";
import { enqueueFailedSubmit, flushFailedSubmits, isRetryable } from "./site/offline-queue";
import { VirtualJoystick } from "./ui/joystick";
import "./ui/styles.css";

const PB_KEY = "aether.splinter_pb";
const HANDLE_KEY = "aether.handle";

flushFailedSubmits("splinter");

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startSplinterRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/splinter", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishSplinterRun(runId: string, handle: string, inputs: SplinterResult["inputs"]) {
  const url = `/api/runs/splinter/${runId}`;
  const body = { handle, inputs };
  try {
    const res = await fetch(url, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (isRetryable(res.status)) enqueueFailedSubmit("splinter", url, body);
      return null;
    }
    return await res.json();
  } catch {
    enqueueFailedSubmit("splinter", url, body);
    return null;
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudKills = document.getElementById("hud-kills")!;
const hudTime = document.getElementById("hud-time")!;
const hudAlive = document.getElementById("hud-alive")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;

let game: Splinter | null = null;
let activeRunId: string | null = null;
let pendingResult: SplinterResult | null = null;

// Mobile: VirtualJoystick on bottom-right (default). Tank-controls
// mapping: stick X (>0.4 / <-0.4) → rot ±1; stick Y > 0.4 (push
// forward) → thrust. FIRE + HYPER chips live in HTML on bottom-left.
const TOUCH_ROT_THRESHOLD = 0.4;
const TOUCH_THRUST_THRESHOLD = 0.4;
const joystick = new VirtualJoystick(document.body, (x, y) => {
  if (!game) return;
  const rot: -1 | 0 | 1 =
    x > TOUCH_ROT_THRESHOLD ? 1 :
    x < -TOUCH_ROT_THRESHOLD ? -1 : 0;
  const thrust = y > TOUCH_THRUST_THRESHOLD;
  game.setTouchInput(rot, thrust);
});
void joystick;

async function startRun() {
  if (game) game.dispose();
  const runStart = await startSplinterRun();
  activeRunId = runStart?.run_id ?? null;
  const seed = runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  game = new Splinter(canvas, {
    onHud: (runMs, kills, alive, scoreEstimate) => {
      hudKills.textContent = kills.toString();
      hudTime.textContent = ((RUN_MS - runMs) / 1000).toFixed(1);
      hudAlive.textContent = alive.toString();
      hudScore.textContent = scoreEstimate.toLocaleString();
    },
    onGameOver: async (result) => {
      hud.dataset.state = "over";
      msgTitle.textContent = result.died ? "Hull breach" : "Survived";
      const hyperNote = result.hyperspace_hops > 0 ? ` · ${result.hyperspace_hops} hyperspace` : "";
      msgBody.textContent = `${result.scoreEstimate.toLocaleString()} pts · ${result.kills} kills${hyperNote}`;
      btnStart.textContent = "Try again";
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (result.scoreEstimate > pb) localStorage.setItem(PB_KEY, String(result.scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "splinter", score: result.scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishSplinterRun(activeRunId, handle, result.inputs);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
      } else {
        pendingResult = result;
        submitForm.hidden = false;
      }
    },
  }, seed);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Splinter";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);
submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingResult) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishSplinterRun(activeRunId, value, pendingResult.inputs);
  pendingResult = null;
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});
window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
